/**
 * @fileoverview Hook UI Layer for calculating relative time in ConversationItemMC.
 * @module useConversationItemTimeUI
 * @category Hook - UI Layer
 * @since 3.1.0
 * @version 3.1.0
 *
 * @description
 * Hook specialized UI Layer for calculating the relative time of a date in the current language, optimized for ConversationItemMC.
 *
 * **Responsibility:**
 * - Only presentation of the relative time for each conversation
 * - No business logic or data manipulation
 * - Integration with i18n for language
 * - Memoization with useCallback
 *
 * @function useConversationItemTimeUI
 * @category Hook - UI Layer
 *
 * @returns {function} howLongAgo - Memoized function to get the relative time
 *
 * @example
 * const howLongAgo = useConversationItemTimeUI();
 * const texto = howLongAgo(conversation.last_message_at);
 *
 * @see ConversationItemMC
 */
import { useCallback } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// Hook UI Layer for calculating relative time in ConversationItemMC
/**
 * Before: in invalid cases moment returned something like "Invalid date" (locale es).
 * Now: in those cases there is no time label; the customer name and the rest of the item remain the same.
 * Only the gray/blue text of the hour disappears until a valid recent_message_date arrives (p. ej. after refreshing the list or a well-formed new_message).
 */
export default function useConversationItemTimeUI() {
  const { i18n } = useTranslation();
  const howLongAgo = useCallback((date) => {
    if (date == null || date === '') {
      return '';
    }

    const parsed = moment(date);
    if (!parsed.isValid()) {
      return '';
    }

    moment.locale(i18n.language);
    return parsed.fromNow(true);
  }, [i18n.language]);
  return howLongAgo;
}
